import {
  Box,
  Button,
  Card,
  Divider,
  FormControl,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  TextField
} from "@material-ui/core";
import CardSpacer from "@saleor/components/CardSpacer";
import Container from "@saleor/components/Container";
import PageHeader from "@saleor/components/PageHeader";
import useNavigator from "@saleor/hooks/useNavigator";
import { sectionNames } from "@saleor/intl";
import { MotherOrderList_orders_edges_node } from "@saleor/motherOrders/types/MotherOrderList";
import { NonRemittanceSummary_nonRemittanceSummary } from "@saleor/remittance/types/NonRemittanceSummary";
import {
  RemittanceListQueryParams,
  remittanceListUrl
} from "@saleor/remittance/url";
import RemittanceStatusOverride from "@saleor/remittanceRunner/components/RemittanceStatusOverride";
import { RemittanceMotherOrder } from "@saleor/remittanceRunner/utils/formDataTypes";
import { PageListProps } from "@saleor/types";
import { canOverrideNonLegacyCourier } from "@saleor/utils/handlers/userHandler";
import React, { ChangeEvent } from "react";
import { useIntl } from "react-intl";

import RemittanceInfoCard from "../remittanceInfo";
import PandaGoRemittanceList from "../remittanceList/PandaGoRemittanceList";

export interface RemittanceListPageProp extends PageListProps {
  remittance: MotherOrderList_orders_edges_node[];
  params: RemittanceListQueryParams;
  nonRemittanceSummary: NonRemittanceSummary_nonRemittanceSummary;
  download: () => void;
  refetchPandagoRemittance: () => void;
}

const useStyles = makeStyles(
  theme => ({
    buttonContainer: {
      width: "28%"
    },
    cardContainer: {
      display: "grid",
      gridColumnGap: theme.spacing(2),
      gridTemplateColumns: "1fr",
      [theme.breakpoints.down("sm")]: {
        gridColumnGap: theme.spacing(1),
        gridTemplateColumns: "1fr"
      }
    },
    cardContainer2: {
      display: "grid",
      gridColumnGap: theme.spacing(2),
      gridTemplateColumns: "1fr 1fr",
      [theme.breakpoints.down("sm")]: {
        gridColumnGap: theme.spacing(1),
        gridTemplateColumns: "1fr"
      }
    },
    cardSpacing: {
      alignItems: "center",
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      padding: theme.spacing(2, 3),
      ...{
        "& button": {
          minHeight: "3.25rem"
        }
      },
      [theme.breakpoints.down("md")]: {
        flexDirection: "column",
        marginRight: 0,
        ...{
          "& button": {
            width: "100%"
          },
          "& div": {
            marginBottom: theme.spacing(1),
            marginRight: 0,
            width: "100%"
          }
        }
      },
      [theme.breakpoints.down("sm")]: {
        marginBottom: theme.spacing(1)
      }
    },
    filterContainer: {
      marginRight: theme.spacing(2),
      width: "60%"
    },
    statusOverrideBtn: {
      "& span": {
        color: "#0030FF"
      },
      backgroundColor: "#FFDB00",
      marginBottom: 8,
      marginRight: 32,
      marginTop: 16,
      padding: "5px 10px"
    },
    statusOverrideBtnDisabled: {
      "& span": {
        color: "#0030FF"
      },
      backgroundColor: "#ffdb004d",
      marginBottom: 8,
      marginRight: 32,
      marginTop: 16,
      opacity: 0.7,
      padding: "5px 10px",
      pointerEvents: "none"
    }
  }),
  { name: "RemittanceListPage" }
);

const RemittanceListPage: React.FC<RemittanceListPageProp> = ({
  params,
  nonRemittanceSummary,
  download,
  settings,
  refetchPandagoRemittance,

  ...listProps
}) => {
  const intl = useIntl();
  const classes = useStyles("");
  const navigate = useNavigator();

  const [selectedCourier, setSelectedCourier] = React.useState("Pandago");
  const [openStatusOverride, setOpenStatusOverride] = React.useState(false);
  const [motherOrderList, setMotherOrderList] = React.useState<string[]>([]);
  const [motherOrderCode, setMotherOrderCode] = React.useState<
    RemittanceMotherOrder
  >({
    courier: "",
    orderCode: ""
  });

  const userCanOverrideRemittance = canOverrideNonLegacyCourier();
  const handleCompanyChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    const company = event.target.value as string;
    setSelectedCourier(company);
    {
      /* Placeholder for future enhancements */
    }
    // navigate(remittanceListUrl({ ...params, company }));
  };

  const handleOrderDate = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    navigate(remittanceListUrl({ ...params, orderDate: e.target.value }));
  };

  return (
    <Container>
      <PageHeader title={intl.formatMessage(sectionNames.remittance)} />
      <div className={classes.cardContainer}>
        <Card className={classes.cardSpacing}>
          <div className={classes.filterContainer}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Company</InputLabel>
              <Select
                value={selectedCourier}
                onChange={handleCompanyChange}
                fullWidth
              >
                {/* Placeholder for future enhancements */}
                {/* <MenuItem value="Grab">Grab</MenuItem> */}
                <MenuItem value="Pandago">Pandago</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div className={classes.filterContainer}>
            <TextField
              label={"Date"}
              InputProps={{
                type: "date"
              }}
              value={params.orderDate || null}
              onChange={handleOrderDate}
              fullWidth
            />
          </div>
          <div className={classes.buttonContainer}>
            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                download();
              }}
              size="small"
              fullWidth
            >
              Download CSV
            </Button>
          </div>
        </Card>
      </div>
      <CardSpacer />
      <div className={classes.cardContainer2}>
        <RemittanceInfoCard
          title={"Ongoing and Cancelled F&B Orders"}
          icon={undefined}
        >
          {nonRemittanceSummary?.foodAndBeveragesTotalPending}
        </RemittanceInfoCard>
        <RemittanceInfoCard
          title={"GM Orders in Shipped Status"}
          icon={undefined}
        >
          {nonRemittanceSummary?.gmTotalPendingShipped}
        </RemittanceInfoCard>
      </div>
      <CardSpacer />
      <Card>
        {userCanOverrideRemittance && (
          <>
            <Box display="flex" justifyContent="flex-end" mb={1}>
              <Button
                className={
                  !userCanOverrideRemittance || motherOrderList.length === 0
                    ? classes.statusOverrideBtnDisabled
                    : classes.statusOverrideBtn
                }
                size="small"
                disabled={
                  !userCanOverrideRemittance || motherOrderList.length === 0
                }
                onClick={() => {
                  setOpenStatusOverride(true);
                }}
              >
                Status Override
              </Button>
            </Box>
            <Divider variant="fullWidth" />
          </>
        )}
        <PandaGoRemittanceList
          remittanceIdList={motherOrderList}
          userCanOverrideRemittance={userCanOverrideRemittance}
          setMotherOrderList={setMotherOrderList}
          setMotherOrderCode={setMotherOrderCode}
          {...listProps}
          settings={settings}
        />
      </Card>
      {openStatusOverride && (
        <RemittanceStatusOverride
          open={openStatusOverride}
          onClose={() => setOpenStatusOverride(false)}
          remittanceOrderCourier={motherOrderCode}
          setRemittanceOrderCourier={setMotherOrderCode}
          bulkOrderIds={motherOrderList}
          setBulkOrderIds={setMotherOrderList}
          onRefetchRemittanceOrder={refetchPandagoRemittance}
          isFromNonLegacy={true}
        />
      )}
    </Container>
  );
};

export default RemittanceListPage;
